import { PUBLISH_STATUS_SOLD, PUBLISH_STATUS_IN_LEGALS } from "./properties";

// Buyer-facing availability rules for the property detail CTAs and the
// reserved/sold badges. Kept out of the pages so every wording and visibility
// rule is unit tested without rendering — mirrors reservationDisplay.ts.
//
// Sold is the stronger, admin-declared state: it disables the CTAs and its
// label/badge wins over "reserved". This is presentation only — the server
// (tva-api) is the actual enforcement (409 PROPERTY_NOT_AVAILABLE).

export interface PropertyAvailabilityInput {
  reserved?: boolean;
  publishStatus?: string;
}

export interface PropertyCtaState {
  disabled: boolean;
  offerLabel: string;
  reserveLabel: string;
}

export interface AvailabilityBadges {
  showSold: boolean;
  showReserved: boolean;
}

export function isSoldStatus(publishStatus?: string): boolean {
  return publishStatus === PUBLISH_STATUS_SOLD;
}

export function isInLegalsStatus(publishStatus?: string): boolean {
  return publishStatus === PUBLISH_STATUS_IN_LEGALS;
}

export function getPropertyCtaState({ reserved, publishStatus }: PropertyAvailabilityInput): PropertyCtaState {
  if (isSoldStatus(publishStatus)) {
    return { disabled: true, offerLabel: "Not Available", reserveLabel: "Property Sold" };
  }
  // In legals is past the point of a new reservation or offer: same disabled
  // state as reserved, labelled for what it is. The API also flags inLegals as
  // reserved for bundles that predate this label.
  if (isInLegalsStatus(publishStatus)) {
    return { disabled: true, offerLabel: "Not Available", reserveLabel: "In Legals" };
  }
  if (reserved === true) {
    return { disabled: true, offerLabel: "Not Available", reserveLabel: "Property Reserved" };
  }
  return { disabled: false, offerLabel: "Make an Offer", reserveLabel: "Make Reservation" };
}

export function getAvailabilityBadges({ reserved, publishStatus }: PropertyAvailabilityInput): AvailabilityBadges {
  const showSold = isSoldStatus(publishStatus);
  return {
    showSold,
    // Sold suppresses the Reserved badge so a property never shows both.
    // In legals sits in the reserved family, so it shows the Reserved badge.
    showReserved: !showSold && (reserved === true || isInLegalsStatus(publishStatus)),
  };
}
